import {
  Box,
  ClickAwayListener,
  Divider,
  InputAdornment,
  ListItemIcon,
  MenuList,
  Paper,
  Popover,
  Popper,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import type { Instance as PopperInstance } from '@popperjs/core';
import { MoreVertical, Plus, Search } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShortcutBadge } from '@shared/ui';
import * as S from './styles';
import type { PopoverMenuItem, PopoverMenuProps, PopoverMenuSubmenuItem } from './types';

// ─── Submenu positioning ──────────────────────────────────────────────────────

/**
 * SUBMENU POSITIONING — the rule, and why it is not MUI's default.
 *
 * The submenu is placed against the **popover panel**, never against the ⋮ button that opens it:
 *
 *   - **Horizontally** it sits beside the whole panel with a 4px gap, on the side with room.
 *     `right-start` is preferred; `flip` swaps to `left-start` when the viewport cannot take it,
 *     keeping the same gap. Anchoring to the button instead (MUI's default, and what this
 *     component used to do) made the submenu overlap the panel it belongs to and jump horizontally
 *     depending on how wide the row's label happened to be.
 *   - **Vertically** its top lines up with the top of the row that owns the button — the only
 *     alignment between the two panels. `preventOverflow` therefore runs with `altAxis: false`:
 *     nudging the submenu up to fit a short viewport would silently break that alignment, so the
 *     rule wins and a very long submenu is allowed to run past the fold.
 *
 * Both are expressed through a Popper VIRTUAL ELEMENT whose rect is stitched from two real ones —
 * X from the panel, Y from the row. Doing it this way means Popper's own collision detection makes
 * the left/right decision; nothing here measures the viewport by hand.
 *
 * The 4px is measured off the design: submenu left 588 − panel right 584 (Figma 37927-93963).
 */
const SUBMENU_GAP = 4;

/**
 * How close a collision modifier may push the submenu to the viewport edge.
 *
 * Separate from `SUBMENU_GAP` because they answer different questions: that one is the
 * designed distance between two panels of the same menu, this one is breathing room
 * against the window. They were one constant, so tightening the panel gap to the
 * design's 4px would silently have let the submenu sit 4px from the screen edge too.
 */
const SUBMENU_VIEWPORT_PADDING = 8;

/** `ListWrapper`'s padding — how far a row sits inside the panel's edge. */
const ROW_INSET = 8;

/** Marks a row as the vertical anchor for its submenu. */
const ROW_ATTR = 'data-popover-menu-row';

/** Marks the menu's own surface, so a right-click inside it is never retargeted. */
const PAPER_ATTR = 'data-popover-menu-paper';

// ─── Right-click retargeting ──────────────────────────────────────────────────

/**
 * What the user was actually aiming at, from under the open modal.
 *
 * `pointer-events: none` on the modal root takes the backdrop AND the panel out of
 * hit-testing for the length of one synchronous call, so the browser resolves the
 * point against the page itself — clipping, stacking contexts and sticky overlaps all
 * settled by the engine. The obvious alternative, scanning the candidates' own rects,
 * gets every one of those wrong the moment a target scrolls out of its container: a
 * header row scrolled up under a toolbar still has a rect sitting on that toolbar, and
 * a right-click there would "hit" a header the user cannot even see.
 */
function elementUnderModal(
  modalRoot: HTMLElement,
  clientX: number,
  clientY: number,
): Element | null {
  const restore = modalRoot.style.pointerEvents;
  modalRoot.style.pointerEvents = 'none';
  try {
    return document.elementFromPoint(clientX, clientY);
  } finally {
    modalRoot.style.pointerEvents = restore;
  }
}

/**
 * A row wrapped in its hover hint, when it carries one — a list row or the new option.
 *
 * The bubble hangs off the LEFT of the panel with its arrow pointing back at the row
 * (Figma) — hence `placement='left'` against the row's full rect. The negative offset
 * cancels `ListWrapper`'s 8px inset so the arrow tip meets the panel's edge instead of
 * stopping short of it inside the padding.
 */
function withRowHint(
  row: React.ReactElement,
  key: string,
  tooltip: React.ReactNode | undefined,
  testId: string | undefined,
): React.ReactElement {
  if (tooltip == null) return row;
  return (
    <Tooltip
      key={key}
      title={tooltip}
      placement='left'
      arrow
      slotProps={{
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, -ROW_INSET] } }],
        },
      }}
    >
      <S.RowTooltipTarget data-testid={testId}>{row}</S.RowTooltipTarget>
    </Tooltip>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Generic, fully-configurable popover menu.
 *
 * Mirrors the structure of `RequestsMenu` but is driven entirely through props
 * so it can be reused anywhere in the app.
 *
 * @example
 * <PopoverMenu
 *   triggerLabel="New"
 *   title="Templates"
 *   titleIcon={HelpCircle}
 *   items={[
 *     { id: '1', label: 'Template A', submenu: [...] },
 *   ]}
 *   newOption={{ label: 'New template', shortcut: 'N', onClick: handleNew }}
 * />
 */
const PopoverMenu: React.FC<PopoverMenuProps> = ({
  disabled = false,
  selectedItemIds = [],
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  transformOrigin = { vertical: 'top', horizontal: 'left' },
  anchorEl: controlledAnchorEl,
  onClose,
  renderTrigger,
  triggerLabel,
  title,
  titleIcon: TitleIcon,
  triggerIcon,
  triggerIconColor,
  triggerIconFill,
  triggerSx,
  triggerTestId,
  onTitleIconAction,
  onOpenChange,
  search,
  searchPlaceholder = 'Search…',
  emptyContent,
  onSearch,
  items,
  newOption,
  width = 340,
  maxListHeight,
  footer,
  className,
  fullWidth = false,
  contextMenuTargetSelector,
  avoidTriggerOverlap = false,
  placement = 'bottom-start',
  elevation = 1,
  modeless = false,
  onMouseEnter,
  onMouseLeave,
  rootTestId,
}) => {
  const { t } = useTranslation('dashboard', { keyPrefix: 'global' });

  // ── Submenu state ──────────────────────────────────────────────────────────
  // The ROW that opened the submenu, not the ⋮ button — see SUBMENU POSITIONING below.
  const [submenuRow, setSubmenuRow] = useState<HTMLElement | null>(null);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const isSubmenuOpen = Boolean(submenuRow);
  /** The menu panel itself. Supplies the X edge the submenu sits beside. */
  const panelRef = useRef<HTMLDivElement | null>(null);
  /** PopperJS instance behind the submenu, so its placement can be recomputed on demand. */
  const submenuPopperRef = useRef<PopperInstance>(null);

  // ── Popover state ──────────────────────────────────────────────────────────
  // Passing `anchorEl` (even as `null`) hands the open state to the caller — the
  // context-menu mode. Without it the menu owns its own anchor, set by a trigger click.
  const isControlled = controlledAnchorEl !== undefined;
  const [internalAnchorEl, setInternalAnchorEl] = useState<HTMLElement | null>(null);
  const anchorEl = isControlled ? controlledAnchorEl : internalAnchorEl;
  const open = Boolean(anchorEl);

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (disabled) return;
      setInternalAnchorEl(e.currentTarget);
      setQuery('');
      onOpenChange?.(true);
    },
    [disabled, onOpenChange],
  );

  const handleClose = useCallback(() => {
    if (isControlled) onClose?.();
    else setInternalAnchorEl(null);
    setActiveItemId(null);
    // Drop the submenu with its parent. One controlled menu is reused across many
    // anchors, so a submenu left anchored to the previous row would otherwise stay
    // on screen after the menu that owns it has closed.
    setSubmenuRow(null);
    setQuery('');
    onOpenChange?.(false);
  }, [isControlled, onClose, onOpenChange]);

  /**
   * A right-click that landed on the modal backdrop — see `contextMenuTargetSelector`
   * for what this is for. Bound to the Popover root, which is the backdrop's parent.
   */
  const handleBackdropContextMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!contextMenuTargetSelector) return;
      // A right-click INSIDE the panel is the panel's own business — selecting text in
      // the search field, the browser menu on a link. Only the backdrop is retargeted.
      if ((e.target as Element | null)?.closest(`[${PAPER_ATTR}]`)) return;
      // No hit-testing in this environment (jsdom ships neither `elementFromPoint` nor
      // `elementsFromPoint`): leave the event alone rather than swallow it blind.
      if (typeof document.elementFromPoint !== 'function') return;

      // Past here the click is ours either way. The browser's own menu must not open on
      // top of ours, and this menu is going away whether or not a target is found —
      // dismiss-on-right-click is what a desktop context menu does.
      e.preventDefault();
      handleClose();

      const target = elementUnderModal(e.currentTarget, e.clientX, e.clientY)?.closest<HTMLElement>(
        contextMenuTargetSelector,
      );
      /**
       * Re-fire on the anchor as though the backdrop had never been there, rather than
       * re-anchoring this instance directly. Two reasons it has to be the event:
       *
       *   - The anchor's own `onContextMenu` is the ONE way a menu opens here, so the
       *     sibling gets whatever else that handler does (suppressing the native menu,
       *     leaving the selection alone) for free, and there is no second open path to
       *     keep in step with the first.
       *   - The sibling is frequently a DIFFERENT `PopoverMenu` instance — the view
       *     chips mount one menu per chip — which this one cannot re-anchor at all.
       *
       * The close above and the open this triggers are both setState inside one React
       * event, so they batch into a single render: the menu MOVES to the new anchor
       * instead of blinking closed and back.
       */
      target?.dispatchEvent(
        new MouseEvent('contextmenu', {
          bubbles: true,
          cancelable: true,
          clientX: e.clientX,
          clientY: e.clientY,
          button: 2,
          buttons: 2,
        }),
      );
    },
    [contextMenuTargetSelector, handleClose],
  );

  const handleOpenSubmenu = useCallback((e: React.MouseEvent<HTMLButtonElement>, id: string) => {
    e.stopPropagation();
    // Walk up to the row: the submenu is placed against the row and the panel, never against
    // the button. `closest` is on an attribute this component owns, not a test id or a MUI class.
    setSubmenuRow(e.currentTarget.closest<HTMLElement>(`[${ROW_ATTR}]`) ?? e.currentTarget);
    setActiveItemId(id);
  }, []);

  const handleCloseSubmenu = useCallback(() => {
    setSubmenuRow(null);
    setActiveItemId(null);
  }, []);

  /** Escape closes the submenu only. Focus lives inside the Popper, which is a sibling of the
   *  parent Popover's modal — so the event never reaches it and the parent menu stays open. */
  const handleSubmenuKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleCloseSubmenu();
      }
    },
    [handleCloseSubmenu],
  );

  // ── Search state ───────────────────────────────────────────────────────────
  const [query, setQuery] = useState('');

  const handleQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      onSearch?.(e.target.value);
    },
    [onSearch],
  );

  // ── Filtered items ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.trim().toLowerCase();
    return items.filter((item) => item.label?.toLowerCase().includes(q));
  }, [items, query]);

  /**
   * `undefined` (the prop omitted) falls back to the default copy; an explicit `null`
   * renders NO empty state at all. The distinction matters for a menu that offers a
   * "create" row: when a search matches nothing but the row invites you to create it,
   * the list is not a dead end and "No options available" contradicts the affordance
   * sitting right under it.
   */
  const resolvedEmptyContent = useMemo(
    () =>
      emptyContent === undefined ? t('popover_menu_empty', 'No options available') : emptyContent,
    [emptyContent, t],
  );

  // ── Item click ─────────────────────────────────────────────────────────────
  const handleItemClick = useCallback(
    (item: PopoverMenuItem) => {
      item.onClick?.();
      if (item.closeOnClick !== false) {
        handleClose();
      }
    },
    [handleClose],
  );

  const handleSubmenuItemClick = useCallback(
    (subItem: PopoverMenuSubmenuItem) => {
      subItem.onClick?.();
      handleCloseSubmenu();
      handleClose();
    },
    [handleClose, handleCloseSubmenu],
  );

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, item: PopoverMenuItem) => {
      // Opt-in rows open their submenu on a full-row click (anchored to the row);
      // every other row keeps the default behaviour of running its click handler.
      if (item.openSubmenuOnRowClick && item.submenu != null && item.submenu.length > 0) {
        e.stopPropagation();
        setSubmenuRow(e.currentTarget);
        setActiveItemId(item.id);
        return;
      }
      handleItemClick(item);
    },
    [handleItemClick],
  );

  const activeItem = useMemo(() => items.find((i) => i.id === activeItemId), [items, activeItemId]);

  /**
   * The stitched rect described in SUBMENU POSITIONING. Read live inside `getBoundingClientRect`
   * rather than captured on open, so the submenu follows the panel if the page scrolls or the list
   * reflows while it is open — Popper re-invokes this on every update.
   */
  const submenuAnchorEl = useMemo(() => {
    if (!submenuRow) return null;

    return {
      getBoundingClientRect: () => {
        const row = submenuRow.getBoundingClientRect();
        // Fall back to the row when the panel has not mounted yet: a slightly tighter anchor is
        // better than a submenu positioned at 0,0.
        const panel = panelRef.current?.getBoundingClientRect() ?? row;

        return {
          x: panel.left,
          y: row.top,
          top: row.top,
          bottom: row.top + row.height,
          left: panel.left,
          right: panel.right,
          width: panel.width,
          height: row.height,
          toJSON() {
            return this;
          },
        } as DOMRect;
      },
    };
  }, [submenuRow]);

  /**
   * Re-place the submenu for as long as the parent menu might still be moving.
   *
   * Popper positions once on mount and then only on scroll/resize. The parent Popover opens with a
   * MUI `Grow`, which animates a `transform: scale(...)` on its paper — and `getBoundingClientRect`
   * reports transformed geometry. So a ⋮ clicked before that transition settles (easy to do: the
   * button appears on hover, and Grow runs ~250ms) measured a shrunken panel, and the submenu
   * stayed wherever that half-scaled rect put it — overlapping the panel instead of beside it.
   * Measured on the static build before this loop existed: 76px of overlap and 36px of vertical
   * drift on a fast click.
   *
   * A bounded rAF loop rather than a `transitionend` listener: it covers the Grow, any reflow of
   * the list behind it, and a scroll landing in the same window, without this component having to
   * know which element MUI happens to animate or for how long.
   */
  useEffect(() => {
    if (!isSubmenuOpen) return;

    const SETTLE_MS = 400;
    const started = performance.now();
    let frame = requestAnimationFrame(function tick() {
      // `update()` returns a promise; the next frame re-runs it anyway, so it is fire-and-forget.
      void submenuPopperRef.current?.update();
      if (performance.now() - started < SETTLE_MS) frame = requestAnimationFrame(tick);
    });

    return () => cancelAnimationFrame(frame);
  }, [isSubmenuOpen]);

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }

      const key = e.key;

      // Submenu shortcuts take priority when the submenu is open
      if (isSubmenuOpen && activeItem?.submenu) {
        for (const sub of activeItem.submenu) {
          if (!sub.shortcut || sub.disabled) continue;
          if (key.toLowerCase() === sub.shortcut.toLowerCase()) {
            e.preventDefault();
            handleSubmenuItemClick(sub);
            return;
          }
        }
      }

      // Main item shortcuts
      for (const item of items) {
        if (item.divider || item.isListGroupTitle || item.disabled || !item.shortcut) continue;
        if (key.toLowerCase() === item.shortcut.toLowerCase()) {
          e.preventDefault();
          handleItemClick(item);
          return;
        }
      }

      // newOption shortcut
      if (newOption?.shortcut && !newOption.disabled) {
        if (key.toLowerCase() === newOption.shortcut.toLowerCase()) {
          e.preventDefault();
          newOption.onClick?.();
          handleClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    open,
    isSubmenuOpen,
    activeItem,
    items,
    newOption,
    handleItemClick,
    handleSubmenuItemClick,
    handleClose,
  ]);

  // ── Trigger ────────────────────────────────────────────────────────────────
  // A controlled menu is opened by whatever the caller anchors it to, so it needs
  // no trigger of its own unless one was explicitly supplied.
  const trigger = renderTrigger ? (
    renderTrigger({ onClick: handleOpen, open })
  ) : isControlled ? null : (
    <S.TriggerButton
      className={className}
      isActive={open}
      disabled={disabled}
      onClick={handleOpen}
      aria-haspopup='true'
      aria-expanded={open}
      data-testid={triggerTestId ?? 'popover-menu-trigger'}
      sx={triggerSx}
      fullWidth={fullWidth}
    >
      {triggerIcon && (
        <Box
          component={triggerIcon}
          size={16}
          color={triggerIconColor ?? 'grey.50'}
          sx={{
            flexShrink: 0,
            ...(triggerIconFill && {
              '& svg': { fill: 'currentColor' },
            }),
          }}
        />
      )}
      {triggerLabel}
    </S.TriggerButton>
  );

  /** One list row, wrapped in its hover hint when the item carries a `tooltip`. */
  const renderRow = (item: PopoverMenuItem) => {
    const row = (
      <S.ListRow
        key={item.id}
        clickable={!item.disabled}
        isActive={(isSubmenuOpen && activeItemId === item.id) || selectedItemIds.includes(item.id)}
        isDanger={item.isDanger}
        disabled={item.disabled}
        onClick={(e) => handleRowClick(e, item)}
        onMouseEnter={item.onMouseEnter}
        onMouseLeave={item.onMouseLeave}
        sx={{ alignItems: item.alignItems ?? 'center' }}
        data-testid={`menu-item-${item.id}`}
        {...{ [ROW_ATTR]: '' }}
      >
        {item.leftIcon != null && (
          <Box
            component={item.leftIcon}
            size={16}
            color={item.isDanger ? 'error.main' : (item.leftIconColor ?? 'grey.50')}
            sx={{
              flexShrink: 0,
              ...(item.leftIconFill && {
                '& svg': { fill: 'currentColor' },
              }),
            }}
            aria-hidden
          />
        )}

        {item.renderLabel ?? <S.ItemText>{item.label}</S.ItemText>}

        <Stack direction='row' spacing={0.5}>
          {item.rightContent}
          {item.shortcut != null && (
            <ShortcutBadge isDanger={item.isDanger}>{item.shortcut}</ShortcutBadge>
          )}

          {item.submenu != null &&
            item.submenu.length > 0 &&
            (item.staticSubmenuIcon ? (
              // A static chevron is signage, not a control: the row already
              // carries the click, so it renders as a plain glyph with no
              // button chrome and stays visible without hovering.
              <S.StaticSubmenuIcon aria-hidden>
                {item.submenuIcon ?? <MoreVertical size={14} />}
              </S.StaticSubmenuIcon>
            ) : (
              <S.ActionIconWrapper
                size='small'
                className='item-action-button'
                aria-label={`${item.label} options`}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleOpenSubmenu(e, item.id)}
                data-testid={`submenu-trigger-${item.id}`}
              >
                {item.submenuIcon ?? <MoreVertical size={14} />}
              </S.ActionIconWrapper>
            ))}
        </Stack>
      </S.ListRow>
    );

    return withRowHint(row, item.id, item.tooltip, item.tooltipTestId);
  };

  const menuContent = (
    <S.PopoverContainer ref={panelRef} style={{ width }} data-testid='popover-menu-panel'>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      {title != null && (
        <S.SectionTitle>
          {title}
          {TitleIcon != null && (
            <S.TitleIconButton
              size='small'
              onClick={onTitleIconAction}
              data-testid='title-icon-button'
              aria-label={title}
            >
              <TitleIcon size={16} />
            </S.TitleIconButton>
          )}
        </S.SectionTitle>
      )}

      {/* ── Search ─────────────────────────────────────────────────────── */}
      {search && (
        <TextField
          fullWidth
          variant='standard'
          placeholder={searchPlaceholder}
          value={query}
          onChange={handleQueryChange}
          autoFocus
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment sx={{ ml: 2, mr: 0 }} position='end'>
                  <Search size={16} />
                </InputAdornment>
              ),
            },
            htmlInput: {
              'data-testid': 'popover-menu-search-input',
            },
          }}
        />
      )}

      {/* ── Item list ──────────────────────────────────────────────────── */}
      <S.ListWrapper style={maxListHeight ? { maxHeight: maxListHeight } : undefined}>
        {filtered.length === 0 ? (
          resolvedEmptyContent === null ? null : (
            <S.EmptyState data-testid='popover-menu-empty-state'>
              {resolvedEmptyContent}
            </S.EmptyState>
          )
        ) : (
          filtered.map((item) =>
            item.divider ? (
              <S.StyledDivider key={item.id} />
            ) : item.isListGroupTitle ? (
              <Stack
                key={item.id}
                direction='row'
                alignItems={item.alignItems ?? 'center'}
                justifyContent='space-between'
                sx={{ px: 1, ...(item.bordered && { borderBottom: 1, borderColor: 'divider' }) }}
              >
                <S.GroupTitle>{item.label}</S.GroupTitle>
                {item.rightContent}
              </Stack>
            ) : (
              renderRow(item)
            ),
          )
        )}

        {/* ── New option ──────────────────────────────────────────────── */}
        {newOption != null && (
          <>
            <Divider sx={{ my: 0.5 }} />
            {withRowHint(
              <S.ListRow
                clickable={!newOption.disabled}
                disabled={newOption.disabled}
                onClick={
                  newOption.disabled
                    ? undefined
                    : () => {
                        newOption.onClick?.();
                        handleClose();
                      }
                }
                data-testid='new-option'
              >
                <Box
                  component={newOption.icon ?? Plus}
                  size={18}
                  color='grey.50'
                  sx={{ flexShrink: 0 }}
                  aria-hidden
                />
                <S.ItemText>{newOption.label}</S.ItemText>
                {newOption.shortcut != null && <ShortcutBadge>{newOption.shortcut}</ShortcutBadge>}
              </S.ListRow>,
              'new-option',
              newOption.tooltip,
              newOption.tooltipTestId,
            )}
          </>
        )}
      </S.ListWrapper>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      {footer}
    </S.PopoverContainer>
  );

  return (
    <>
      {trigger}

      {/* ── Main Popover ───────────────────────────────────────────────────── */}
      {avoidTriggerOverlap ? (
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement={placement}
          onClick={(e) => e.stopPropagation()}
          sx={{ zIndex: (theme) => theme.zIndex.modal }}
          modifiers={[
            { name: 'offset', options: { offset: [0, 8] } },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top-start', 'right-start', 'left-start', 'bottom-start'],
              },
            },
            { name: 'preventOverflow', options: { padding: 8 } },
          ]}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Paper
              elevation={elevation}
              sx={{
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {menuContent}
            </Paper>
          </ClickAwayListener>
        </Popper>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          onClick={(e) => e.stopPropagation()}
          // Lands on the Popover's root, i.e. the backdrop's parent — a right-click
          // anywhere off the panel bubbles here. No-ops unless a caller opted in.
          onContextMenu={contextMenuTargetSelector ? handleBackdropContextMenu : undefined}
          // The submenu Popper renders OUTSIDE this modal's DOM, so while it is open the modal's
          // focus trap would keep yanking focus back out of it — the submenu's arrow keys and its
          // Escape handling both depend on holding focus. Relaxed only while the submenu is open;
          // the trap is back the moment it closes. (The `avoidTriggerOverlap` branch is a bare
          // Popper with no modal, so it never had the problem.)
          disableEnforceFocus={modeless || isSubmenuOpen}
          // A hover surface (`modeless`): a backdrop would eat the pointer the moment it left
          // the anchor, and taking focus would blur whatever the user is typing in behind it.
          hideBackdrop={modeless}
          disableAutoFocus={modeless}
          disableRestoreFocus={modeless}
          disableScrollLock={modeless}
          // The theme already defaults MuiPopover to elevation 1; passing it explicitly keeps the
          // two surfaces tied to one value that a caller can move together.
          // `PAPER_ATTR` is spread in because a `data-*` key is only special-cased on JSX
          // attributes, not inside an object literal TS checks for excess properties.
          slotProps={{
            ...(modeless && { root: { sx: { pointerEvents: 'none' } } }),
            paper: {
              elevation,
              ...{ [PAPER_ATTR]: '' },
              ...(modeless && { sx: { pointerEvents: 'auto' }, onMouseEnter, onMouseLeave }),
            },
          }}
          data-testid={rootTestId}
        >
          {menuContent}
        </Popover>
      )}

      {/* ── Submenu ────────────────────────────────────────────────────────── */}
      {/* A Popper, not a Menu: the placement rule in SUBMENU POSITIONING needs the flip
          modifier and a virtual anchor, neither of which MUI's anchored `Menu` exposes.
          One z-index above the parent's modal, so the parent's backdrop cannot eat the clicks. */}
      <Popper
        open={isSubmenuOpen}
        anchorEl={submenuAnchorEl}
        popperRef={submenuPopperRef}
        placement='right-start'
        onClick={(e) => e.stopPropagation()}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        modifiers={[
          { name: 'offset', options: { offset: [0, SUBMENU_GAP] } },
          {
            name: 'flip',
            options: { fallbackPlacements: ['left-start'], padding: SUBMENU_VIEWPORT_PADDING },
          },
          // `altAxis: false` is the rule, not a default: vertical nudging would break the
          // submenu-top ↔ row-top alignment this whole component is positioned around.
          {
            name: 'preventOverflow',
            options: { altAxis: false, padding: SUBMENU_VIEWPORT_PADDING },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleCloseSubmenu}>
          <S.SubmenuPaper
            elevation={elevation}
            onKeyDown={handleSubmenuKeyDown}
            data-testid='popover-menu-submenu'
          >
            {/*
              Mirrors the panel: same 8px inset, and the panel's width as a FLOOR rather
              than a fixed size (see `SubmenuList`). Fixed, it could neither grow nor
              clip — a row wider than the panel (a status plus its "via sign-off" hint at
              width 220) simply painted outside the paper.
            */}
            <S.SubmenuList style={typeof width === 'number' ? { minWidth: width } : undefined}>
              <MenuList autoFocusItem={isSubmenuOpen} variant='menu' sx={{ p: 0 }}>
                {activeItem?.submenu?.map((subItem, index) => (
                  <React.Fragment key={subItem.id}>
                    {(subItem.separator ||
                      (subItem.isDanger &&
                        index > 0 &&
                        !activeItem.submenu![index - 1].isDanger)) && <Divider sx={{ my: 0.5 }} />}
                    <S.StyledMenuItem
                      isDanger={subItem.isDanger}
                      disabled={subItem.disabled}
                      onClick={() => handleSubmenuItemClick(subItem)}
                      data-testid={`submenu-item-${subItem.id}`}
                    >
                      {subItem.leftIcon != null && (
                        <ListItemIcon>
                          <subItem.leftIcon size={16} color={subItem.leftIconColor} />
                        </ListItemIcon>
                      )}
                      {/*
                        Same text guards as a panel row (`renderRow`): the label is the
                        part that gives way — it ellipsizes inside the paper — while the
                        right-hand content keeps its full width. Without them a MenuItem
                        (no `overflow: hidden`) lets its children spill past the paper edge.
                      */}
                      <S.ItemText>{subItem.label}</S.ItemText>
                      {subItem.shortcut != null && (
                        <ShortcutBadge>{subItem.shortcut}</ShortcutBadge>
                      )}
                      {subItem.rightContent != null && (
                        <Box
                          component='span'
                          sx={{ ml: 'auto', pl: 2, display: 'inline-flex', flexShrink: 0 }}
                        >
                          {subItem.rightContent}
                        </Box>
                      )}
                    </S.StyledMenuItem>
                  </React.Fragment>
                ))}
              </MenuList>
            </S.SubmenuList>
          </S.SubmenuPaper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default PopoverMenu;
export type {
  PopoverMenuItem,
  PopoverMenuNewOption,
  PopoverMenuProps,
  PopoverMenuSubmenuItem,
} from './types';
